import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import {
  HelpCircle, GitBranch, Sun, Columns3, ContactRound, ClipboardCheck,
  LayoutDashboard, BarChart3, Flame, FileText, Receipt, UserCog, Star,
  Activity, DollarSign, Settings, Palette, ShieldCheck, HeartPulse,
  ChevronDown, Search, Sparkles, Mail, CreditCard, MessageSquare, Package,
  Users, Wand2, Bell,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/help")({
  head: () => ({ meta: [
    { title: "Help & Guide — Admin" },
    { name: "robots", content: "noindex,nofollow" },
  ]}),
  component: HelpPage,
});

type Section = {
  id: string;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  summary: string;
  body: React.ReactNode;
};

const FUNNEL_STEPS = [
  { step: "1", name: "Visitor lands", where: "Public site (/, /gallery, /services, /studio)", tracked: "Analytics · Heat Maps · Funnels (gallery view)" },
  { step: "2", name: "Engages", where: "Gallery project page, Color Studio, or Estimate CTA", tracked: "Funnels (project view, cta click)" },
  { step: "3", name: "Submits AI estimate", where: "/estimate wizard", tracked: "CRM → AI Estimates · Contacts (auto-created) · Pipeline (New Lead)" },
  { step: "4", name: "Reviewed by admin", where: "Admin → CRM (Pipeline / AI Estimates tab)", tracked: "Lead notes, stage change, task assignment" },
  { step: "5", name: "Proposal sent", where: "Admin → Proposals (Generate from estimate)", tracked: "Proposal activity log, customer email" },
  { step: "6", name: "Customer approves", where: "Public proposal link → /proposal/$token", tracked: "Proposal status = approved, Pipeline advances" },
  { step: "7", name: "Job & invoicing", where: "Admin → Customer Projects · Accounting → Invoices", tracked: "Invoices, payments, expenses, profitability" },
  { step: "8", name: "Review request", where: "Auto-triggered after job complete", tracked: "Reviews (Google/Facebook clicks, private feedback)" },
];

const SECTIONS: Section[] = [
  {
    id: "today", title: "Today", icon: Sun,
    summary: "Daily command center — new leads, today's tasks, upcoming appointments, and quick actions.",
    body: (
      <>
        <p>Landing page every time you sign in. Shows what needs attention today: unread messages, unassigned leads, tasks due, and appointments on the calendar.</p>
        <ul className="list-disc pl-5 space-y-1 text-sm text-zinc-400">
          <li>Click any lead card to jump into the contact detail drawer.</li>
          <li>Task checkboxes mark complete in place — no page reload.</li>
          <li>Use the quick "+" to add a task or log a call without leaving.</li>
        </ul>
      </>
    ),
  },
  {
    id: "notifications", title: "Notifications (bell icon)", icon: Bell,
    summary: "Bell icon in the top bar of both admin and customer portal — real-time activity feed and unread counter.",
    body: (
      <>
        <p>Every admin page and every customer portal page now shows a bell icon at the top. Click it to open the activity feed with recent notifications (new leads, proposal views/approvals, payments, messages, review submissions, etc). Unread items show a red dot on the bell.</p>
        <ul className="list-disc pl-5 space-y-1 text-sm text-zinc-400">
          <li><b>Admin bell</b> — surfaces business events: new AI estimate, proposal approved/declined/changes-requested, invoice paid, new review, urgent private feedback.</li>
          <li><b>Customer portal bell</b> — surfaces their job events: proposal ready, invoice sent, message from the team, schedule updates.</li>
          <li>Configure email/push preferences in <b>Notification Settings</b> (admin sidebar and portal profile menu).</li>
        </ul>
      </>
    ),
  },
  {
    id: "crm", title: "CRM (Pipeline · Contacts · AI Estimates)", icon: Users,
    summary: "One grouped section with three tabs covering every lead and customer relationship.",
    body: (
      <>
        <p>The CRM cluster lives under one sidebar entry and switches between tabs at the top of the page:</p>
        <ul className="list-disc pl-5 space-y-1 text-sm text-zinc-400">
          <li><b>Pipeline</b> — kanban of every lead by stage (New Lead → Contacted → Estimate Sent → Proposal Sent → Won / Lost). Drag cards to change stage; every AI Estimate lands here as "New Lead".</li>
          <li><b>Contacts</b> — master list of every person the business has interacted with. Auto-populated from AI Estimates, contact form, proposal approvals, and manual entry. Click a contact for the full timeline (estimates, proposals, invoices, messages, notes, tasks). Dedupes by email.</li>
          <li><b>AI Estimates</b> — the raw feed of estimate submissions from the /estimate wizard. Generate a proposal, jump to the auto-created contact, or archive from here.</li>
        </ul>
        <p className="text-sm text-zinc-400">All three tabs read from the same underlying people and estimates — they're different views, not separate datasets. The old duplicate "Leads" tab has been retired.</p>
      </>
    ),
  },
  {
    id: "tasks", title: "Tasks", icon: ClipboardCheck,
    summary: "Follow-ups, callbacks, site visits, and any to-do tied to a contact or project.",
    body: <p>Filter by owner, due date, or contact. Tasks created from a contact card auto-link back so nothing gets orphaned.</p>,
  },
  {
    id: "overview", title: "Overview", icon: LayoutDashboard,
    summary: "High-level KPIs: revenue this month, active jobs, close rate, average deal size.",
    body: <p>Super-admin only. Rolls up Accounting + Pipeline + Reviews into one dashboard.</p>,
  },
  {
    id: "analytics", title: "Analytics (Traffic · Heat Maps · Funnels)", icon: BarChart3,
    summary: "Grouped analytics section with three tabs for site behavior and conversion.",
    body: (
      <>
        <p>All three analytics views live under one sidebar entry with tabs across the top:</p>
        <ul className="list-disc pl-5 space-y-1 text-sm text-zinc-400">
          <li><b>Traffic</b> — site traffic, top pages, referrers, device breakdown, and session recordings. Powered by the built-in analytics tracker (page_view + interaction events).</li>
          <li><b>Heat Maps</b> — click and scroll overlays per public page. Pick a page + date range; overlay renders on top of a live-page screenshot.</li>
          <li><b>Funnels</b> — conversion funnel <i>gallery view → project view → cta click → estimate started → estimate submitted</i>. Each step is a tracked event with count and step-to-step conversion %.</li>
        </ul>
        <p className="text-sm text-zinc-400">If a funnel step stays at 0, open the page and watch the network tab for an <code>ingestEvents</code> call. No call = tracker not mounted. Wrong event name = mismatch in <code>src/lib/analytics/track.ts</code>.</p>
      </>
    ),
  },
  {
    id: "proposals", title: "Proposals", icon: FileText,
    summary: "Send branded, itemized proposals customers can approve online.",
    body: (
      <>
        <p>Create from scratch, from a template, or from an AI Estimate. Each proposal has a public <code>/proposal/$token</code> link — customer can view, request changes, approve, or decline.</p>
        <ul className="list-disc pl-5 space-y-1 text-sm text-zinc-400">
          <li>Line items, discounts, taxes, and paint colors all in one document.</li>
          <li>Activity log records every view, approval, and change request.</li>
          <li>Approved proposals auto-advance the pipeline stage.</li>
          <li><b>Save & Send after edits</b> — when you edit a proposal (including responding to a "changes requested") and click Save & Send, the customer receives a fresh email every time. Follow-up sends are no longer deduplicated, and the status automatically flips back to <b>Sent</b> (with a new sent timestamp) unless the proposal is already approved, declined, or void — those stay locked.</li>
        </ul>
      </>
    ),
  },
  {
    id: "accounting", title: "Accounting", icon: Receipt,
    summary: "Invoices, payments, deposits, expenses, receivables, profitability, and reports.",
    body: (
      <>
        <p>Sub-sections:</p>
        <ul className="list-disc pl-5 space-y-1 text-sm text-zinc-400">
          <li><b>Invoices</b> — create, send, print, or take Stripe card payment.</li>
          <li><b>Payments</b> — record checks, ACH, cash, or reconcile Stripe payouts.</li>
          <li><b>Deposits</b> — bank deposit batches for bookkeeping.</li>
          <li><b>Expenses</b> — job-costed spend by category and vendor.</li>
          <li><b>Receivables</b> — aging report for unpaid invoices.</li>
          <li><b>Profitability</b> — revenue minus expenses per job.</li>
          <li><b>Reports</b> — P&L, sales tax, and CSV exports.</li>
          <li><b>Settings</b> — tax rate, invoice numbering, payment instructions.</li>
        </ul>
      </>
    ),
  },
  {
    id: "customers", title: "Customer Projects", icon: UserCog,
    summary: "Every customer project — the record that ties proposal, invoices, photos, colors, and portal access together.",
    body: (
      <>
        <p>Opening a project shows all attached data plus the invite link to the customer portal. Customers sign in there to see colors, photos, invoices, messages, and warranty.</p>
        <p className="text-sm text-zinc-400">Send the portal invite from the project detail page — the customer claims it with their email. (Formerly "Leads & Customers"; the duplicate Leads sub-tab now lives under CRM → AI Estimates.)</p>
      </>
    ),
  },
  {
    id: "reviews", title: "Reviews", icon: Star,
    summary: "Post-job review requests. 5-star ratings route to Google/Facebook; low ratings route to private feedback.",
    body: <p>Requests auto-send after job completion. Track opens, ratings, click-throughs to review sites, and private feedback that needs a follow-up call.</p>,
  },
  {
    id: "ai-estimator", title: "AI Estimator (AI Usage · Pricing)", icon: Wand2,
    summary: "Grouped section with two tabs for tuning and monitoring the AI estimator.",
    body: (
      <>
        <ul className="list-disc pl-5 space-y-1 text-sm text-zinc-400">
          <li><b>AI Usage</b> — daily and monthly spend on Lovable AI (estimates, color renders, assistant chat). Watch this to catch runaway usage. Budget limits live in <code>ai-budget.server.ts</code>.</li>
          <li><b>Pricing</b> — base prices, labor rates, paint markups, and add-ons the AI estimator uses. Changes recalibrate every future estimate; existing estimates keep their original numbers.</li>
        </ul>
      </>
    ),
  },
  {
    id: "settings", title: "Settings", icon: Settings,
    summary: "Business info, review site URLs, service areas, spam thresholds, and integrations.",
    body: <p>Super-admin only. Changes here affect the public site and email templates.</p>,
  },
  {
    id: "users", title: "Users", icon: Palette,
    summary: "Team members with admin or super_admin roles. Add or revoke access here.",
    body: <p>Roles live in a separate <code>user_roles</code> table — safe from privilege-escalation attacks. Only super_admins can promote or demote.</p>,
  },
  {
    id: "audit", title: "Audit Log", icon: ShieldCheck,
    summary: "Immutable record of admin actions — role changes, deletions, setting updates.",
    body: <p>Used for security reviews and troubleshooting "who changed what".</p>,
  },
  {
    id: "health", title: "Health", icon: HeartPulse,
    summary: "System diagnostics: database, email queue, cron jobs, Stripe connection, storage.",
    body: <p>First place to look when something feels broken. Green = healthy, amber = degraded, red = down.</p>,
  },
];

function HelpPage() {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState<string | null>("today");
  const q = query.trim().toLowerCase();
  const filtered = q
    ? SECTIONS.filter(s =>
        s.title.toLowerCase().includes(q) || s.summary.toLowerCase().includes(q))
    : SECTIONS;

  return (
    <div className="max-w-4xl">
      <div className="mb-8">
        <p className="text-amber-300 text-[10px] tracking-[0.3em] uppercase font-semibold flex items-center gap-2">
          <HelpCircle className="size-3.5" /> Admin Guide
        </p>
        <h1 className="font-serif text-3xl lg:text-4xl text-white mt-2">How the admin panel works</h1>
        <p className="text-zinc-400 mt-3 max-w-2xl">
          Reference for every section of the admin, plus the full lead-to-cash sales funnel.
          Use this whenever something is unclear or when onboarding a new team member.
        </p>
      </div>

      {/* Funnel diagram */}
      <section className="mb-10 rounded-2xl border border-amber-300/20 bg-amber-300/[0.03] p-6">
        <h2 className="font-serif text-xl text-white flex items-center gap-2 mb-1">
          <GitBranch className="size-4 text-amber-300" /> The sales funnel, end to end
        </h2>
        <p className="text-sm text-zinc-400 mb-5">
          From an anonymous visitor to a paid, reviewed job — every step, where it lives, and how it's tracked.
        </p>
        <ol className="space-y-2">
          {FUNNEL_STEPS.map((s) => (
            <li key={s.step} className="grid grid-cols-[auto_minmax(0,1fr)] gap-4 items-start rounded-xl bg-black/30 border border-white/5 p-3">
              <span className="size-8 rounded-full bg-amber-300 text-zinc-950 grid place-items-center text-sm font-bold">{s.step}</span>
              <div>
                <p className="text-white font-medium text-sm">{s.name}</p>
                <p className="text-[11px] text-zinc-500 mt-0.5"><b className="text-zinc-400">Where:</b> {s.where}</p>
                <p className="text-[11px] text-zinc-500"><b className="text-zinc-400">Tracked in:</b> {s.tracked}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* Quick concepts */}
      <section className="mb-10 grid sm:grid-cols-2 gap-3">
        <Concept icon={ContactRound} title="Contact vs. Customer Project">
          A <b>contact</b> is anyone in the CRM (lead or past client). A <b>customer project</b> is a specific job with a portal login, proposal, colors, and invoices attached.
        </Concept>
        <Concept icon={FileText} title="Estimate vs. Proposal">
          An <b>AI estimate</b> is the auto-generated price range from the /estimate wizard. A <b>proposal</b> is the branded, itemized document you send for signature.
        </Concept>
        <Concept icon={CreditCard} title="Invoice statuses">
          <b>Draft → Sent → Viewed → Partially Paid → Paid</b>. Past-due invoices flip to <b>Overdue</b> automatically.
        </Concept>
        <Concept icon={Mail} title="Automatic emails">
          Sent for: estimate received, proposal sent, invoice sent/paid, review request, portal invite. All templates live in <code>src/lib/email-templates/</code>.
        </Concept>
        <Concept icon={MessageSquare} title="Customer Portal">
          Every customer project auto-creates a portal at <code>/portal/*</code>. They see colors, photos, invoices, messages, and warranty for their job only (RLS-enforced).
        </Concept>
        <Concept icon={Package} title="Roles">
          <b>super_admin</b> sees everything and can manage users/settings. <b>admin</b> sees day-to-day ops (Today, Pipeline, Contacts, Proposals, Accounting).
        </Concept>
      </section>

      {/* Section-by-section */}
      <section className="mb-10">
        <div className="flex items-center justify-between gap-3 mb-4">
          <h2 className="font-serif text-xl text-white">Every section, explained</h2>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-zinc-500" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              className="bg-black/40 border border-white/10 rounded-full pl-8 pr-3 py-1.5 text-xs w-48 focus:border-amber-300/50 outline-none"
            />
          </div>
        </div>

        <div className="space-y-2">
          {filtered.map((s) => {
            const Icon = s.icon;
            const isOpen = open === s.id;
            return (
              <div key={s.id} className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
                <button
                  onClick={() => setOpen(isOpen ? null : s.id)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/[0.03]"
                >
                  <Icon className="size-4 text-amber-300 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-white text-sm font-medium">{s.title}</p>
                    <p className="text-[11px] text-zinc-500 truncate">{s.summary}</p>
                  </div>
                  <ChevronDown className={`size-4 text-zinc-500 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>
                {isOpen && (
                  <div className="px-4 pb-4 pt-1 text-sm text-zinc-300 space-y-2 border-t border-white/5">
                    {s.body}
                  </div>
                )}
              </div>
            );
          })}
          {filtered.length === 0 && (
            <p className="text-sm text-zinc-500 text-center py-8">No sections match "{query}".</p>
          )}
        </div>
      </section>

      {/* Troubleshooting */}
      <section className="mb-10 rounded-2xl border border-white/10 bg-white/[0.02] p-6">
        <h2 className="font-serif text-xl text-white mb-4">Common issues</h2>
        <dl className="space-y-4 text-sm">
          <Trouble q="A new AI Estimate isn't showing up in Contacts or Pipeline">
            Estimate submission upserts a contact automatically. If missing, check <b>Health</b> for database errors and confirm the estimate row exists in AI Estimates. Backfill by opening the estimate and clicking "Sync to CRM".
          </Trouble>
          <Trouble q="Funnel steps stuck at 0">
            Each step is a page/event pair. Open the page in a browser, watch the network tab for a <code>ingestEvents</code> call. No call = tracker not mounted. Wrong event name = mismatch in <code>src/lib/analytics/track.ts</code>.
          </Trouble>
          <Trouble q="Customer can't see their invoice / colors">
            They must sign in to the portal with the exact email the invite went to. Re-send the portal invite from Customer Projects → project → "Send portal invite".
          </Trouble>
          <Trouble q="Emails aren't arriving">
            Check <b>Health → Email queue</b>. If pending items are stuck, the sending domain may not be DNS-verified yet — that's handled in Project Settings → Email.
          </Trouble>
          <Trouble q="Payment came in but invoice still shows a balance">
            Payments must be recorded against the invoice, not as a standalone deposit. Open Accounting → Payments → New, and pick the invoice from the dropdown.
          </Trouble>
          <Trouble q="I edited a proposal and clicked Save & Send but the customer didn't get a new email">
            Fixed — every Save & Send now issues a fresh email (no deduplication) and resets the status to <b>Sent</b> with a new sent timestamp. If it still doesn't arrive: verify the customer email on the proposal, check <b>Health → Email queue</b>, and confirm the address isn't on the suppression list. Approved / declined / void proposals stay locked and won't resend.
          </Trouble>
          <Trouble q="Customer portal or admin bell isn't showing new activity">
            The bell polls on page load and in the background. Refresh once; if still empty, open <b>Notification Settings</b> and confirm the event type is enabled. Server events (proposal viewed, invoice paid) also flow through here — check <b>Health</b> if the underlying event isn't firing.
          </Trouble>
        </dl>
      </section>

      <p className="text-xs text-zinc-500 text-center">
        Missing something? <Link to="/admin/settings" className="text-amber-300 underline">Settings</Link> or ping the developer.
      </p>
    </div>
  );
}

function Concept({ icon: Icon, title, children }: { icon: React.ComponentType<{ className?: string }>; title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
      <p className="flex items-center gap-2 text-white text-sm font-medium mb-1">
        <Icon className="size-4 text-amber-300" /> {title}
      </p>
      <p className="text-xs text-zinc-400 leading-relaxed">{children}</p>
    </div>
  );
}

function Trouble({ q, children }: { q: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-white font-medium mb-1">{q}</dt>
      <dd className="text-zinc-400 text-[13px] leading-relaxed">{children}</dd>
    </div>
  );
}